/**
 * Personal passwords (docs/members.md "Personal passwords"): the pure parts the server and both
 * clients share. What the server says, the held-name rule the middleware runs on every request,
 * the sign-in limit's key, and the words both clients show. Imports only other shared files: this
 * runs in a Pages Function, the Preact app, the native app and Node's test runner.
 */
import { MEMBER_MESSAGES, householdPasswordError, passwordRepeatError } from "./members.ts";
import { PASSKEY_MESSAGES } from "./passkeys.ts";
import { normalizeName } from "./peons.ts";

/** What the server says when a password route or the held-name rule refuses; the clients show it as is. */
export const PASSWORD_MESSAGES = {
  /** 403, code name_held: this device used a name someone holds without signing in as them. */
  nameHeld: "That name has a password here. Sign in as them, or pick another name.",
  /** 401 from POST /api/auth/person/login, for an unknown name and a wrong password alike. */
  wrongLogin: "Wrong name or password",
  /** 429 once an address has failed PERSON_LOGIN_FAIL_LIMIT times this hour. */
  tooManyTries: "Too many tries. Wait an hour.",
  /** 400: the claiming rule needs a name (the passkey words, the same rule). */
  pickName: PASSKEY_MESSAGES.pickName,
  /** 409: a peon device may not claim a steward's name (the passkey words, the same rule). */
  stewardName: PASSKEY_MESSAGES.stewardName,
  /** 409: the name's person holds a passkey or a password. */
  nameTaken: "Someone already holds that name here. Sign in as them first, or pick another name.",
  /** 401: changing a password needs the one it replaces. */
  wrongCurrent: "Wrong current password",
  stewardsOnly: MEMBER_MESSAGES.stewardsOnly,
  /** 404 from DELETE /api/house/people/:id/password. */
  noSuchPerson: "That person is not in this household",
  /** 403 from POST /api/peons/rename: a held name moves only for its person or a steward. */
  renameHeld: "That name has a password. Only its person or a steward can rename it.",
} as const;

/** The `code` of the held-name refusal, beside its `error` and the held `name`. */
export const NAME_HELD_CODE = "name_held";

/** The body of a 403 name_held answer. */
export interface NameHeldBody {
  error: string;
  code: typeof NAME_HELD_CODE;
  /** The held name, in its registered spelling. */
  name: string;
}

export function nameHeldBody(name: string): NameHeldBody {
  return { error: PASSWORD_MESSAGES.nameHeld, code: NAME_HELD_CODE, name };
}

/** Whether an error body is the held-name refusal. */
export function isNameHeldBody(v: unknown): v is NameHeldBody {
  if (!v || typeof v !== "object") return false;
  const b = v as Record<string, unknown>;
  return b.code === NAME_HELD_CODE && typeof b.name === "string";
}

// ---------- Held names ----------

/** A held name and the person who holds it. */
export interface HeldName {
  name: string;
  personId: string;
}

/** The entry of `held` that `name` is (normalized, ignoring case, as names match everywhere), or null. */
export function heldNameFor<T extends { name: string }>(held: readonly T[], name: string): T | null {
  const n = normalizeName(name).toLowerCase();
  if (!n) return null;
  return held.find((h) => normalizeName(h.name).toLowerCase() === n) ?? null;
}

/**
 * The held-name rule (functions/_middleware.ts): a request naming a held name in `x-peon-name` is
 * refused unless its session is bound to the person who holds it. Returns the held name refused,
 * or null when the request may go on (no name, a free name, or the device's own person).
 */
export function nameHeldRefusal(held: readonly HeldName[], name: string, sessionPersonId: string | null): HeldName | null {
  const hit = heldNameFor(held, name);
  if (!hit || (sessionPersonId !== null && hit.personId === sessionPersonId)) return null;
  return hit;
}

// ---------- The sign-in limit ----------

/** Failed sign-ins one address may make in an hour; the next attempt answers 429. */
export const PERSON_LOGIN_FAIL_LIMIT = 30;
/** The most people of one name a sign-in checks the password against (across households). */
export const PERSON_LOGIN_MAX_MATCHES = 5;
export const PERSON_LOGIN_FAIL_PREFIX = "person_login_fail:";

/**
 * The counter of an address's failed sign-ins this hour: `person_login_fail:<ipHash>:<hour>`, where
 * `ipHash` is the one the creation limit uses (16 hex of the SHA-256 of the address) and the hour
 * is `YYYY-MM-DDTHH`, UTC.
 */
export function personLoginFailKey(ipHash: string, now: Date): string {
  return `${PERSON_LOGIN_FAIL_PREFIX}${ipHash}:${now.toISOString().slice(0, 13)}`;
}

// ---------- The password rule ----------

/** Why `password` cannot be a person's password, or null: the household password's rule, 8 to 200 characters. */
export const personPasswordError: (password: unknown) => string | null = householdPasswordError;

/** The clients' check of a new password and its "Again" before asking the server. */
export function newPasswordError(password: string, again: string): string | null {
  return personPasswordError(password) ?? passwordRepeatError(password, again);
}

// ---------- Words (both clients) ----------

export const PASSWORD_COPY = {
  /** The gate's first form. */
  signInLine: "Sign in with your own password.",
  nameField: "Your name",
  passwordField: "Your password",
  enter: "Enter",
  /** The gate's link to today's household password form. */
  useHousehold: "Use the household password",
  /** Under the name gate. */
  nameGateLine: "A name with a password belongs to its person. Set yours in your peon's sheet.",
  /** The account sheet's row. */
  rowLabel: "Password",
  set: "Set a password",
  setLine: "So no one else can be you.",
  change: "Change password",
  fieldPassword: "Password",
  fieldAgain: "Again",
  fieldCurrent: "Current",
  fieldNew: "New",
  save: "Save",
  saved: "Password saved",
  /** The House screen's people. */
  holds: "Password",
  holdsNot: "No password",
  remove: "Remove password",
} as const;

/** Under a held name at the name gate and in Change name: "Jonathan has a password." */
export function hasPasswordLine(name: string): string {
  return `${name} has a password.`;
}

/** The House screen's confirmation before a steward removes someone's password. */
export function removePasswordConfirm(name: string): string {
  return `Remove ${name}'s password? The name is free again until they set one.`;
}
